// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { format, isValid } from "date-fns";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  CustomSelect,
  SimpleAsyncSelect,
} from "../../../components/CustomFields/CustomSelect";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";
import CustomBreadcrumbs from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import PhoneInput from "react-phone-number-input";
import DatePickerComponent from "../../../components/CustomFields/DatePicker";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import dayjs from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import {
  CreateVaccineTemplate,
  GetVaccineTemplateDetail,
  UpdateVaccineTemplate,
  GetVaccineTemplateList,
} from "../../../services/VaccineTemplateService";
import {
  GetPatientDetail,
  CreatePatient,
  UpdatePatient,
  GetPatientNumber,
  UpdatePatientOPDAppointment,
} from "../../../services/PatientService";
import { PAGE_SIZE } from "../../../services/common/const";
import { GetBranchLanguageList } from "../../../services/LanguageService";
import { GetCountryListForSelect } from "../../../services/CountryService";
import { GetCountryRegionListForSelect } from "../../../services/CountryRegionService";
import { PreferencesContext } from "../../../PreferenceContext";
import PatientVaccineModuleSave from "../PatientVaccineModule/PatientVaccineModuleSave";
import CustomTooltip from "../../../components/ToolTip/CustomToolTip";
import { getAge, handleFocus } from "../../../shared/CommonFunction";
import { createInputPropsForNumberField } from "../../../shared/data";
import {
  GetMemberVaccineModuleList,
  DeleteMemberVaccineModule,
  HandleActivateMemberVaccinationModule,
} from "../../../services/MemberVaccineModuleService";
import {
  GetSpecialMedicalConditionList,
  SaveOPDSpecialMedicalCondition,
  UpdateOPDSpecialMedicalCondition,
} from "../../../services/SpecialMedicalConditionService";
import SpecialMedicalConditionMasterSave from "../../SpecialMedicalCondition/SpecialMedicalConditionMasterSave";
import PatientReportParameter from "../../Settings/Configuration/ReportParameterConfiguration/PatientReportParameter";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}
// SCHEMA FOR PARAMETER
interface Parameter {
  itemId: string;
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  itemId: "",
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};

interface IMedicalCondition {
  displayOrder: number;
  specialMedicalConditionId: number;
  medicalConditionName: string;
  isDeleted?: boolean;
  mode?: string;
}

interface IPatientReportParameter {
  memberPatientReportParameterValueId: number;
  memberPatientReportParameterId: number;
  opdId: number;
  patientId: number;
  investigationId: number;
  investigationName: string | null;
  investigationParameterId: number;
  parameterName: string | null;
  investigationParameterUnitId: number;
  unitName: string | null;
  unitValue: string | null;
  specimen: string | null;
  remarks: string | null;
  entryDateTime: string | null;
  displayOrder: number;
}

// SCHEMA FOR FORM
interface formInitialValueTypes {
  patientId: number;
  patientNumber: string;
  patientAutoNumber: number;
  prefix: selectOption | null;
  prefixName: string | null;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  ageYear: number;
  ageMonth: number;
  ageDay: number;
  preferredLanguage: selectOption | null;
  preferredLanguageName: string | null;
  maritalStatus: selectOption | null;
  maritalStatusName: string | null;
  gender: selectOption | null;
  genderName: string | null;
  location: string;
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
  countryId: selectOption | null;
  countryName: string | null;
  countryRegionId: selectOption | null;
  countryRegionName: string | null;
  email: string;
  primaryMobileNumber: string;
  secondaryMobileNumber: string;
  bloodGroup: selectOption | null;
  bloodGroupName: string | null;
  birthWeight: number | null;
  motherHeight: number | null;
  fatherHeight: number | null;
  hasMediclaim: boolean;
  enableVaccination: boolean;
  nameOfInsuranceCompany: string;
  annualIncomeOfPatientParent: string;
  notes: string;
  isDeleted: boolean;
  specialMedicalConditionColl: Array<IMedicalCondition>;
  patientReportParameterColl: Array<IPatientReportParameter>;
}

// DEFAULT SCHEMA VALUE FOR FORM

const prefixOptions: selectOption[] = [
  { value: "Dr.", label: "Dr." },
  { value: "Mr.", label: "Mr." },
  { value: "Ms.", label: "Ms." },
  { value: "Mrs.", label: "Mrs." },
  { value: "Prof.", label: "Prof." },
];
const bloodgroupOptions: selectOption[] = [
  { value: "A+", label: "A+" },
  { value: "A-", label: "A-" },
  { value: "B+", label: "B+" },
  { value: "B-", label: "B-" },
  { value: "O+", label: "O+" },
  { value: "O-", label: "O-" },
  { value: "AB+", label: "AB+" },
  { value: "AB-", label: "AB-" },
];

const genderOptions: selectOption[] = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
];

const maritalStatusOptions: selectOption[] = [
  { value: "SINGLE", label: "Single" },
  { value: "MARRIED", label: "Married" },
  { value: "DIVORCED", label: "Divorced" },
  { value: "WIDOWED", label: "Widowed" },
  { value: "REMARRIED", label: "Remarried" },
];

interface HeightChartProps {
  motherHeight: number;
  fatherHeight: number;
}

export default function PatientSave(Props: any) {
  const {
    isForPopUp = false,
    handleClosePopUp,
    name,
    opd,
    popUpReason,
    code,
  } = Props;
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawPatientId = urlParams.get("q");
  let currentTab = urlParams.get("t");
  let curPatientId = 0;
  let currTabType = 0;

  if (!isForPopUp) {
    if (curRawPatientId !== null && curRawPatientId !== undefined) {
      let decId = edService.decrypt(curRawPatientId);
      if (decId !== "" && decId !== null && decId !== undefined) {
        curPatientId = +decId;
      }
    }
    // Set tab type from URL parameter
    if (currentTab === "special-medical-condition") {
      currTabType = 1;
    } else if (currentTab === "vaccination") {
      currTabType = 2;
    } else if (currentTab === "report-parameter") {
      currTabType = 3;
    } else {
      currTabType = 0; // default to general-info
    }
  } else {
    if (isForPopUp && opd?.patientId > 0) {
      curPatientId = opd.patientId;
    }
  }

  const getNamePartsFromPopup = (fullName?: string) => {
    const normalizedName = (fullName || "").trim().replace(/\s+/g, " ");
    const parts = normalizedName ? normalizedName.split(" ") : [];

    if (parts.length === 0) {
      return {
        firstName: "",
        middleName: "",
        lastName: "",
      };
    }

    if (parts.length === 1) {
      return {
        firstName: parts[0].toUpperCase(),
        middleName: "",
        lastName: "",
      };
    }

    if (parts.length === 2) {
      return {
        firstName: parts[0].toUpperCase(),
        middleName: "",
        lastName: parts[1].toUpperCase(),
      };
    }

    return {
      firstName: parts[0].toUpperCase(),
      middleName: parts.slice(1, -1).join(" ").toUpperCase(),
      lastName: parts[parts.length - 1].toUpperCase(),
    };
  };

  const popupNameParts = getNamePartsFromPopup(name);

  const vaccinationEnableMode = useMemo(() => {
    return userContext?.systemConfigurationColl?.find(
      (item) =>
        item?.configModule === "VACCINATION" &&
        item?.configKey === "ENABLE_VACCINATION",
    )?.configValue;
  }, [userContext?.systemConfigurationColl]);

  const initPatientData: formInitialValueTypes = {
    patientId: 0,
    patientNumber: "",
    patientAutoNumber: 0,
    prefix: null,
    firstName: isForPopUp ? popupNameParts.firstName : "",
    middleName: isForPopUp ? popupNameParts.middleName : "",
    lastName: isForPopUp ? popupNameParts.lastName : "",
    dateOfBirth: "",
    ageYear: 0,
    ageMonth: 0,
    ageDay: 0,
    preferredLanguage: null,
    maritalStatus: null,
    gender: null,
    location: "",
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    countryId: { label: "INDIA", value: "91" },
    countryRegionId: { label: "Gujarat (24)", value: "7" },
    email: "",
    primaryMobileNumber: "",
    secondaryMobileNumber: "",
    bloodGroup: null,
    hasMediclaim: false,
    enableVaccination: vaccinationEnableMode === "TRUE" ? true : false,
    nameOfInsuranceCompany: "",
    annualIncomeOfPatientParent: "",
    notes: "",
    isDeleted: false,
    countryName: null,
    countryRegionName: null,
    prefixName: null,
    preferredLanguageName: null,
    maritalStatusName: null,
    genderName: null,
    bloodGroupName: null,
    birthWeight: null,
    motherHeight: null,
    fatherHeight: null,
    specialMedicalConditionColl: [],
    patientReportParameterColl: [],
  };

  // PREFERRED DEFAULT LANGUAGE FROM CONFIGURATION
  const PreferredDefaultLanguageConfig = useMemo(() => {
    return userContext?.systemConfigurationColl?.find(
      (item) =>
        item?.configModule === "LANGUAGE" &&
        item?.configKey === "PATIENT_DEFAULT_PREFERRED_LANGUAGE",
    );
  }, [userContext?.systemConfigurationColl]);

  //INPUT PROPS
  const heightProps = createInputPropsForNumberField(3, 1, true);
  const weightProps = createInputPropsForNumberField(3, 3, true);
  // HANDLERS FOR VACCINE MODULE
  const handleOpenVaccineModuleDialog = () => {
    if (curPatientId > 0) {
      setVaccineDialogMode("add");
      setEditingVaccineModule(null);
      setOpenVaccineDialog(true);
    }
  };

  const handleEditModule = (item: any) => {
    if (curPatientId > 0 && item.memberVaccineModuleId > 0) {
      setVaccineDialogMode("edit");
      setEditingVaccineModule(item);
      setOpenVaccineDialog(true);
    }
  };

  const handleVaccineModuleClose = () => {
    setOpenVaccineDialog(false);
    setEditingVaccineModule(null);
    setVaccineDialogMode("add");
  };

  const handleVaccineModuleSuccess = async (code: string, data: any) => {
    if (code === "ADD_MODULE") {
      await getPatientVaccineModuleList();
    }
    handleVaccineModuleClose();
  };

  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE

  const [param, setParam] = useState({ ...parameters, tabType: currTabType });
  const [countryData, setCountryData] = useState<any[]>([]);
  const [countryRegionData, setCountryRegionData] = useState<any[]>([]);
  const [selectedVaccineTemplate, setSelectedVaccineTemplate] =
    useState<any>(null); // Keep for single selection if needed, or remove if fully multi
  const [selectedTemplates, setSelectedTemplates] = useState<any[]>([]);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [vaccineTemplateColl, setVaccineTemplateColl] = useState<Array<any>>(
    [],
  );
  const [patientVaccineModuleColl, setPatientVaccineModuleColl] = useState<
    Array<any>
  >([]);
  const [vaccineBrandDetailColl, setVaccineBrandDetailColl] = useState<
    Array<any>
  >([]);
  const [resultDataCollection, setResultDataCollection] = useState<any[]>([]);
  const [seeMore, setSeeMore] = useState<boolean>(false);

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [selectedModule, setSelectedModule] = React.useState<any>(null);
  const [openDeleteDilog, setOpenDeleteDilog] = React.useState(false);
  const [openVaccineDialog, setOpenVaccineDialog] = React.useState(false);
  const [vaccineDialogMode, setVaccineDialogMode] = React.useState<
    "add" | "edit"
  >("add");
  const [editingVaccineModule, setEditingVaccineModule] =
    React.useState<any>(null);
  const [asyncSelectKey, setAsyncSelectKey] = React.useState({
    medicalConditionSearch: 1,
  });
  const [specialMedicalConditionColl, setSpecialMedicalConditionColl] =
    useState<any[]>([]);

  const [openReportParameter, setOpenReportParameter] = useState(false);

  const handleMenuOpen = (event, item) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedModule(item);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedModule(null);
  };

  const handleDeleteModule = (item: any) => {
    setSelectedModule(item);
    setOpenDeleteDilog(true);
  };
  const showDental = React.useMemo(() => {
    return userContext.allowedSystemColl?.some(
      (system: any) =>
        system.specialityId === userContext.specialityId &&
        system.examinationSystemCode === "DENTAL",
    );
  }, [userContext.allowedSystemColl, userContext.specialityId]);

  const isSuperAdmin =
    (userContext?.loginDetail?.userRole || "").toString().toLowerCase() ===
    "superadmin";

  const confirmDeleteModule = async () => {
    if (selectedModule?.memberVaccineModuleId) {
      let params = {
        deleteMemberVaccineModuleId:
          selectedModule?.memberVaccineModuleId?.toString(),
        consumerId: userContext?.consumerId?.toString(),
        branchId: userContext?.branchId?.toString(),
        orgId: userContext?.organizationId?.toString(),
        isDeleted: true,
        dataManagedBy: userContext.loginDetail.userId,
      };
      setIsLoading(true);
      const res: any = await DeleteMemberVaccineModule(params);
      if (res?.data) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: res?.message,
          alertType: AlertType.Success,
        });
        getPatientVaccineModuleList();
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: res?.data?.message,
          alertType: AlertType.Error,
        });
      }
      setIsLoading(false);
      setOpenDeleteDilog(false);
      setSelectedModule(null);
    }
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    patientId: Yup.number().nullable(),
    patientNumber: Yup.string().required(),
    patientAutoNumber: Yup.number().required(),
    prefix: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    prefixName: Yup.string().nullable(),
    firstName: Yup.string().required("First Name is required"),
    middleName: Yup.string().nullable(),
    lastName: Yup.string().required("Last Name is required"),
    dateOfBirth: Yup.string().required("Date of birth is required"),
    gender: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Gender is required"),
    genderName: Yup.string().nullable(),
    preferredLanguage: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    preferredLanguageName: Yup.string().nullable(),
    maritalStatus: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    maritalStatusName: Yup.string().nullable(),
    location: Yup.string().nullable(),
    address1: Yup.string().nullable(),
    address2: Yup.string().nullable(),
    postalCode: Yup.string().nullable(),
    city: Yup.string().nullable(),
    countryName: Yup.string().nullable(),
    countryRegionName: Yup.string().nullable(),
    countryId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Country is required"),
    countryRegionId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("State is required"),
    email: Yup.string().email("Invalid email").nullable(),
    bloodGroup: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    bloodGroupName: Yup.string().nullable(),
    birthWeight: Yup.number()
      .max(999, "Max weight limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    motherHeight: Yup.number()
      .max(999, "Max MotherHeight limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    fatherHeight: Yup.number()
      .max(999, "Max FatherHeight limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      ),
    primaryMobileNumber: Yup.string().nullable(),
    secondaryMobileNumber: Yup.string().nullable(),
    hasMediclaim: Yup.boolean(),
    enableVaccination: Yup.boolean(),
    nameOfInsuranceCompany: Yup.string(),
    annualIncomeOfPatientParent: Yup.string(),
    notes: Yup.string(),
    isDeleted: Yup.boolean(),
  });

  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
  });

  const handleClickOpen = (value: boolean, label: string, code: string) => {
    setCustomPopUp({ ...customPopUp, open: value, name: label, code: code });
  };

  // const handleClickOpen = (value: boolean, label: string, code: any) => {
  //   setCustomPopUp({ ...customPopUp, open: value, name: label, code: code });
  // };

  const handleClose = () => {
    setCustomPopUp({ ...customPopUp, open: false });
  };

  // VACCINE MODULE DIALOG STATE

  // TAB CAHNGE -- PLEASE USE PARAMETER STATE FOR ANY SEARCH OR ANYTHING THAT AFFECT LIST RESULT
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setParam({ ...param, tabType: newValue });
    if (isForPopUp) {
      return;
    }
    const tabCode =
      newValue === 0
        ? "general-info"
        : newValue === 1
          ? "special-medical-condition"
          : newValue === 2
            ? "vaccination"
            : "report-parameter";
    navigate(
      {
        search: `?q=${encodeURIComponent(
          edService.encrypt(curPatientId.toString()) || "",
        )}&t=${tabCode}`,
      },
      { replace: true },
    );
  };

  const handleOpenReportParameterDialog = () => {
    setOpenReportParameter(true);
  };

  // HANDLE REPORT PARAMETER POPUP
  const handleManagePopup = async (code: string, item: any) => {
    setOpenReportParameter(false);
    setCustomPopUp({ ...customPopUp, open: false });
    setIsLoading(true);
    if (code === "REPORT_PARAMETER") {
      await getPatientDetailById();
    }
    setIsLoading(false);
  };

  // const handleManagePopup = async (code, option) => {
  //   setCustomPopUp({ ...customPopUp, open: false });
  //   if (code === "DOCTOR_CREATE") {
  //     if(option?.doctorTypeCode === 'CONSULTING_DOCTOR'){
  //     methods.setValue(
  //       "consultingDoctorId",
  //       {
  //         label: option?.doctorLable,
  //         value: option?.doctorId?.toString(),
  //       },
  //       {
  //         shouldValidate: true,
  //       }
  //     );
  //   }
  //   if(option?.doctorTypeCode === 'REFERRAL_DOCTOR'){
  //     methods.setValue(
  //       "referralDoctorId",
  //       {
  //         label: option?.doctorLable,
  //         value: option?.doctorId?.toString(),
  //       },
  //       {
  //         shouldValidate: true,
  //       }
  //     );
  //   }
  //     setIsLoading(true);
  //     await fetchConsultingDoctor("", true);
  //     setIsLoading(false);
  //   }

  // };

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initPatientData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  const specialMedicalConditionCollWatch = useWatch({
    control: methods.control,
    name: "specialMedicalConditionColl",
  });

  const patientReportParameterCollWatch = useWatch({
    control: methods.control,
    name: "patientReportParameterColl",
  });

  const filledReportParameters = (patientReportParameterCollWatch ?? []).filter(
    (item: any) =>
      String(item?.unitValue ?? "").trim() !== "" ||
      String(item?.optionValue ?? "").trim() !== "",
  );

  // GET Patient DETAIL
  const getPatientDetailById = async () => {
    let Patient: any;
    let params = new URLSearchParams();
    params.append("patientId", curPatientId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    try {
      const res: any = await GetPatientDetail(params);
      if (res?.data?.data) {
        Patient = res.data.data;

        methods.reset({
          ...initPatientData,
          patientId: Patient?.patientId || 0,
          patientNumber: Patient?.patientNumber || "",
          patientAutoNumber: Patient?.patientAutoNumber || 0,
          prefix: Patient.prefix
            ? { label: Patient.prefixName, value: Patient.prefix }
            : null,
          prefixName: Patient.prefixName || "",
          firstName: Patient?.firstName || "",
          middleName: Patient?.middleName || "",
          lastName: Patient?.lastName || "",
          ageDay: Patient?.ageDay,
          ageMonth: Patient?.ageMonth,
          ageYear: Patient?.ageYear,
          dateOfBirth: Patient?.dateOfBirth || "",
          preferredLanguage: Patient?.preferredLanguage
            ? {
                label: Patient?.preferredLanguageName,
                value: Patient?.preferredLanguage,
              }
            : null,
          preferredLanguageName: Patient?.preferredLanguageName || "",
          maritalStatus: Patient?.maritalStatus
            ? {
                label: Patient?.maritalStatusName,
                value: Patient?.maritalStatus,
              }
            : null,
          maritalStatusName: Patient?.maritalStatusName || "",
          gender: Patient?.gender
            ? {
                label: Patient?.genderName,
                value: Patient?.gender,
              }
            : null,
          genderName: Patient?.genderName || "",
          location: Patient?.location || "",
          address1: Patient?.address1 || "",
          address2: Patient?.address2 || "",
          city: Patient?.city || "",
          postalCode: Patient?.postalCode || "",
          countryId: Patient?.countryId
            ? {
                label: Patient?.countryName || "",
                value: Patient?.countryId?.toString(),
              }
            : null,
          countryName: Patient?.countryName || "",
          countryRegionId: Patient?.countryRegionId
            ? {
                label: Patient?.countryRegionName || "",
                value: Patient?.countryRegionId?.toString(),
              }
            : null,
          countryRegionName: Patient?.countryRegionName || "",
          email: Patient?.email || "",
          primaryMobileNumber: Patient?.primaryMobileNumber || "",
          secondaryMobileNumber: Patient?.secondaryMobileNumber || "",
          bloodGroup: Patient?.bloodGroup
            ? {
                label: Patient?.bloodGroupName || "",
                value: Patient?.bloodGroup?.toString(),
              }
            : null,
          bloodGroupName: Patient?.bloodGroupName || "",
          birthWeight: Patient?.birthWeight,
          motherHeight: Patient?.motherHeight,
          fatherHeight: Patient?.fatherHeight,
          hasMediclaim: Patient?.hasMediclaim || false,
          enableVaccination: Patient?.enableVaccination ?? true,
          nameOfInsuranceCompany: Patient?.nameOfInsuranceCompany || "",
          annualIncomeOfPatientParent:
            Patient?.annualIncomeOfPatientParent || "",
          notes: Patient?.notes || "",
          specialMedicalConditionColl:
            Patient?.specialMedicalConditionColl || [],
          patientReportParameterColl: Patient?.patientReportParameterColl || [],
        });
      }

      return Patient;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE Patient
  const savePatient = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        patientId: value.patientId,
        opdId: opd?.opdId || 0,
        appointmentId: opd?.appointmentId || 0,
        patientNumber: value.patientNumber,
        patientAutoNumber: value.patientAutoNumber,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        prefix: value.prefix?.value,
        prefixName: value.prefixName,
        firstName: value.firstName?.trim() || "",
        middleName: value.middleName?.trim() || "",
        lastName: value.lastName?.trim() || "",
        dateOfBirth: value?.dateOfBirth !== "" ? value?.dateOfBirth : null,
        ageYear: value.ageYear,
        ageMonth: value.ageMonth,
        ageDay: value.ageDay,
        preferredLanguage: value.preferredLanguage?.value,
        preferredLanguageName: value.preferredLanguageName,
        maritalStatus: value.maritalStatus?.value,
        maritalStatusName: value?.maritalStatusName || "",
        gender: value.gender?.value,
        genderName: value.genderName,
        location: value.location?.trim() || "",
        address1: value.address1?.trim() || "",
        address2: value.address2?.trim() || "",
        city: value.city?.trim() || "",
        postalCode: value.postalCode?.trim() || "",
        countryId: value.countryId?.value,
        countryName: value.countryId?.label || "",
        countryRegionId: value.countryRegionId?.value,
        countryRegionName: value.countryRegionId?.label || "",
        email: value.email?.trim() || "",
        primaryMobileNumber: value.primaryMobileNumber?.trim() || "",
        secondaryMobileNumber: value.secondaryMobileNumber?.trim() || "",
        bloodGroup: value.bloodGroup?.value,
        bloodGroupName: value.bloodGroupName || "",
        birthWeight: value.birthWeight || 0,
        motherHeight: value.motherHeight || 0,
        fatherHeight: value.fatherHeight || 0,
        hasMediclaim: value.hasMediclaim,
        enableVaccination: value.enableVaccination,
        nameOfInsuranceCompany: value.nameOfInsuranceCompany?.trim() || "",
        annualIncomeOfPatientParent:
          value.annualIncomeOfPatientParent?.trim() || "",
        notes: value.notes?.trim() || "",
        DataManagedBy: userContext.loginDetail.userId,
      };
      if (curPatientId > 0) {
        if (popUpReason === "OPD_PATIENT_UPDATE") {
          result = await UpdatePatientOPDAppointment(formData);
        } else {
          result = await UpdatePatient(formData);
        }
      } else {
        result = await CreatePatient(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result?.data) {
            if (code === "PATIENT") {
              handleClosePopUp("PATIENT_CREATE", result?.data);
            } else if (code === "REFERRAL_PATIENT") {
              handleClosePopUp("REFERRAL_PATIENT_CREATE", result?.data);
            }
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET COUNTRIES
  async function fetchCountry() {
    try {
      let params = new URLSearchParams();
      const countryData: any = await GetCountryListForSelect(params);
      if (countryData?.data?.data) {
        let countyCollection = countryData.data.data;
        setCountryData(countyCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FETCH STATE FOR COUNTRY
  async function fetchCountryRegionData(countryId) {
    try {
      let params = new URLSearchParams();
      params.append("countryId", countryId);
      const localCountryRegionData: any =
        await GetCountryRegionListForSelect(params);
      if (localCountryRegionData?.data?.data) {
        let stateCollection = localCountryRegionData.data.data;
        setCountryRegionData(stateCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // // GET SELECT OF DOCTOR
  // const fetchConsultingDoctor = async (
  //   searchValue: string,
  //   oninit: boolean
  // ) => {
  //   try {
  //     let params = new URLSearchParams();
  //     params.append("consumerId", userContext.consumerId?.toString());
  //     params.append("orgId", userContext.organizationId?.toString());
  //     params.append("branchId", userContext.branchId?.toString());
  //     params.append("pageIndex", param.pageIndex?.toString());
  //     params.append("pageSize", param.pageSize?.toString());
  //     params.append("sortColumn", param.sortColumn);
  //     params.append("sortDirection", param.sortDirection);
  //     params.append("searchValue", searchValue);
  //     params.append("doctorTypeCodes", "CONSULTING_DOCTOR");
  //     params.append("mode", "DROPDOWN".toString());
  //     const result: any = await GetDoctorList(params);
  //     if (result?.data?.data?.doctorColl) {
  //       let doctorCollection = result.data.data.doctorColl;
  //       // if (oninit) setdoctorConsultingData(doctorCollection);
  //     }
  //     setIsLoading(false);
  //   } catch (e) {
  //     setIsLoading(false);
  //   }
  // };

  // // FILTER REF DOCTOR
  // async function filterConsultingDoctor(inputValue: string) {
  //   let result = await fetchConsultingDoctor(inputValue, false);
  //   return result;
  // }

  // // REF DOCTOR LOAD ON SEARCH
  // const loadConsultingDoctorList = (
  //   inputValue: string,
  //   callback: (options: any) => void
  // ) => {
  //   if (inputValue && inputValue.length > 0) {
  //     filterConsultingDoctor(inputValue).then((r) => {
  //       callback(r);
  //     });
  //   } else {
  //     callback([]);
  //   }
  // };

  // // GET SELECT OF DOCTOR
  // const fetchReferralDoctor = async (
  //   searchValue: string,
  //   oninit: boolean
  // ) => {
  //   try {
  //     let params = new URLSearchParams();
  //     params.append("consumerId", userContext.consumerId?.toString());
  //     params.append("orgId", userContext.organizationId?.toString());
  //     params.append("branchId", userContext.branchId?.toString());
  //     params.append("pageIndex", param.pageIndex?.toString());
  //     params.append("pageSize", param.pageSize?.toString());
  //     params.append("sortColumn", param.sortColumn);
  //     params.append("sortDirection", param.sortDirection);
  //     params.append("searchValue", searchValue);
  //     params.append("doctorTypeCodes", "REFERRAL_DOCTOR");
  //     params.append("departmentIds", "");
  //     params.append("specialityIds", "");
  //     params.append("mode", "DROPDOWN".toString());
  //     const result: any = await GetDoctorList(params);
  //     if (result?.data?.data?.doctorColl) {
  //       let doctorCollection = result.data.data.doctorColl;
  //       if (oninit) setdoctorReferralData(doctorCollection);
  //     }
  //     setIsLoading(false);
  //   } catch (e) {
  //     setIsLoading(false);
  //   }
  // };

  // // FILTER REF DOCTOR
  // async function filterReferralDoctor(inputValue: string) {
  //   let result = await fetchReferralDoctor(inputValue, false);
  //   return result;
  // }

  // // REF DOCTOR LOAD ON SEARCH
  // const loadReferralDoctorList = (
  //   inputValue: string,
  //   callback: (options: any) => void
  // ) => {
  //   if (inputValue && inputValue.length > 0) {
  //     filterReferralDoctor(inputValue).then((r) => {
  //       callback(r);
  //     });
  //   } else {
  //     callback([]);
  //   }
  // };

  // GET PATIENT NUMBER
  const getPatientNumber = async () => {
    try {
      let PatientNumber;
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      const result: any = await GetPatientNumber(params);
      if (result?.data?.data) {
        PatientNumber = result.data.data;
      }
      return PatientNumber;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // // GET DEPARTMENT
  // async function fetchDepartment() {
  //     try {
  //       let params = new URLSearchParams();
  //       params.append("consumerId", userContext?.consumerId?.toString());
  //       params.append("orgId", userContext?.organizationId?.toString());
  //       params.append("branchId", userContext?.branchId?.toString());
  //       params.append("mode", "DROPDOWN".toString());
  //       const departmentData: any = await GetDepartmentList(params);
  //       if (departmentData?.data?.data?.departmentColl) {
  //         let departmentCollection = departmentData.data.data.departmentColl;
  //         setdepartmentData(departmentCollection);
  //       }
  //       // setIsLoading(false);
  //     } catch (e: any) {
  //       updateUserContext({
  //         ...userContext,
  //         isAlert: true,
  //         alertMessage:
  //           typeof e === "string" ? (e as string) : (e?.message as string),
  //         alertType: AlertType.Error,
  //       });
  //       // setIsLoading(false);
  //     }
  //   }

  // // GET SPECIALITY
  //  async function fetchSpeciality(departmentIds: string) {
  //   try {
  //     let params = new URLSearchParams();
  //     params.append("consumerId", userContext.consumerId?.toString());
  //     params.append("orgId", userContext.organizationId?.toString());
  //     params.append("branchId", userContext.branchId?.toString());
  //     params.append("departmentIds", departmentIds);
  //     params.append("mode", "DROPDOWN".toString());
  //     const specialityData: any = await GetSpecialityList(params);
  //     if (specialityData?.data?.data?.specialityColl) {
  //       let specialityCollection = specialityData.data.data.specialityColl;
  //       setspecialityData(specialityCollection);
  //     }
  //   } catch (e: any) {
  //     updateUserContext({
  //       ...userContext,
  //       isAlert: true,
  //       alertMessage:
  //         typeof e === "string" ? (e as string) : (e?.message as string),
  //       alertType: AlertType.Error,
  //     });
  //   }
  // }

  // GET PREFERRED LANGUAGE
  const getLanguageList = async () => {
    try {
      let languageCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      const resultDataCollection: any = await GetBranchLanguageList(params);
      if (resultDataCollection?.data?.data) {
        languageCollection = resultDataCollection.data?.data;
        setResultDataCollection(languageCollection);
      }
      return languageCollection;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET VACCINE TEMPLATE LIST
  const getVaccineTemplateList = async () => {
    let params = new URLSearchParams();
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("pageIndex", param.pageIndex?.toString());
    params.append("pageSize", param.pageSize?.toString());
    params.append("sortColumn", param.sortColumn);
    params.append("sortDirection", param.sortDirection);
    params.append("searchValue", param.searchValue);
    const result: any = await GetVaccineTemplateList(params);
    if (result.data.data && result.data.data.vaccineTemplateColl) {
      const templates = result.data.data.vaccineTemplateColl.map((t: any) => ({
        label: t.templateName,
        value: t.vaccineTemplateId,
      }));
      setVaccineTemplateColl(templates);
    }
  };

  // GET VACCINE TEMPLATE LIST
  const getPatientVaccineModuleList = async () => {
    let params = new URLSearchParams();
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("patientId", curPatientId?.toString());
    params.append("pageIndex", param.pageIndex?.toString());
    params.append("pageSize", param.pageSize?.toString());
    params.append("sortColumn", param.sortColumn);
    params.append("sortDirection", param.sortDirection);
    params.append("searchValue", param.searchValue);
    const result: any = await GetMemberVaccineModuleList(params);
    if (result.data.data && result.data.data.memberVaccinationModuleColl) {
      const templates = result.data.data.memberVaccinationModuleColl;
      setPatientVaccineModuleColl(templates);
    }
  };

  const handleActivateMemberVaccinationModule = async (item: any) => {
    try {
      let result: any = "";
      setIsLoading(true);
      const payload = {
        consumerId: userContext.consumerId,
        orgId: userContext.organizationId,
        branchId: userContext.branchId,
        specialityId: userContext.specialityId,
        departmentId: userContext.departmentId,
        isActive: false,
        vaccineTemplateId: item?.vaccineTemplateId,
        templateName: item?.vaccineTemplateName,
        name: item?.vaccineName,
        dataManagedBy: userContext?.loginDetail?.userId,
      };

      result = await HandleActivateMemberVaccinationModule(payload);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSpecialMedicalCondition = async (item: any) => {
    try {
      const currentColl =
        methods.getValues("specialMedicalConditionColl") || [];

      const remainingNames =
        currentColl
          ?.filter(
            (c: any) =>
              c?.specialMedicalConditionId !== item?.specialMedicalConditionId,
          )
          ?.map((c: any) => c?.medicalConditionName)
          .join(", ") || "";

      let formData = {
        opdSpecialMedicalConditionId: item?.opdSpecialMedicalConditionId || 0,
        specialMedicalConditionId: item?.specialMedicalConditionId || 0,
        opdId: item?.opdId || 0,
        patientId: curPatientId || 0,
        medicalConditionName: remainingNames,
        medicalConditionJson: JSON.stringify([
          {
            displayOrder: item?.displayOrder,
            specialMedicalConditionId: item?.specialMedicalConditionId,
            medicalConditionName: item?.medicalConditionName,
            isDeleted: true,
            mode: "D",
          },
        ]),
        rowManagedBy: userContext?.loginDetail?.userId,
      };

      const result: any = await UpdateOPDSpecialMedicalCondition(formData);

      if (result?.data) {
        const updatedColl = currentColl?.filter(
          (c: any) =>
            c?.specialMedicalConditionId !== item?.specialMedicalConditionId,
        );

        methods.setValue("specialMedicalConditionColl", updatedColl, {
          shouldValidate: true,
        });

        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Deleted.",
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    }
  };

  const handleSpecialMedicalConditionDetailAdd = async (value: any) => {
    const detailColl = methods?.getValues().specialMedicalConditionColl || [];
    const maxRowNumber = detailColl.reduce(
      (acc: number, curr: any) =>
        curr.displayOrder > acc ? curr.displayOrder : acc,
      0,
    );

    const newDisplayOrder = detailColl.length === 0 ? 0 : maxRowNumber + 1;

    const newRow: IMedicalCondition = {
      displayOrder: newDisplayOrder,
      specialMedicalConditionId: Number(value?.value) || 0,
      medicalConditionName: value?.label || "",
      isDeleted: false,
      mode: "I",
    };

    // For existing patient: immediately persist to backend
    if (curPatientId > 0) {
      try {
        setIsLoading(true);
        const existingColl = detailColl;
        const newConditionJson = [
          {
            displayOrder: newDisplayOrder,
            specialMedicalConditionId: Number(value?.value) || 0,
            medicalConditionName: value?.label || "",
            isDeleted: false,
            mode: "I",
          },
        ];

        const formData = {
          opdSpecialMedicalConditionId: 0,
          specialMedicalConditionId: Number(value?.value) || 0,
          opdId: opd?.opdId || 0,
          patientId: curPatientId,
          medicalConditionName: value?.label || "",
          medicalConditionJson: JSON.stringify(newConditionJson),
          rowManagedBy: userContext?.loginDetail?.userId,
        };

        const hasExisting = existingColl.length > 0;
        let result: any;
        if (hasExisting) {
          result = await UpdateOPDSpecialMedicalCondition(formData);
        } else {
          result = await SaveOPDSpecialMedicalCondition(formData);
        }

        if (result?.data) {
          methods.setValue(
            "specialMedicalConditionColl",
            [...detailColl, newRow],
            {
              shouldValidate: true,
            },
          );
          updateUserContext({
            ...userContext,
            isAlert: true,
            alertMessage: result?.message,
            alertType: AlertType.Success,
          });
        } else {
          updateUserContext({
            ...userContext,
            isAlert: true,
            alertMessage: "Something went wrong.",
            alertType: AlertType.Error,
          });
        }
      } catch (e: any) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: typeof e === "string" ? e : e?.message,
          alertType: AlertType.Error,
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      // For new patient: just update local state; will be saved with patient via medicalConditionJson
      methods.setValue("specialMedicalConditionColl", [...detailColl, newRow], {
        shouldValidate: true,
      });
    }
  };

  const handleManageMedicalConditionPopup = async (
    code: string,
    option: any,
  ) => {
    setCustomPopUp({ ...customPopUp, open: false });
    setIsLoading(true);
    if (code === "MEDICAL_CONDITION_CREATE") {
      await fetchMedicalCondition("", true);
      if (option) {
        await handleSpecialMedicalConditionDetailAdd({
          label: option.medicalConditionName,
          value: option.specialMedicalConditionId?.toString(),
        });
      }
    }
    setIsLoading(false);
  };

  // GET MEDICAL CONDITION LIST
  async function fetchMedicalCondition(searchValue: string, oninit: boolean) {
    try {
      let MedicalConditionCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN".toString());
      const result: any = await GetSpecialMedicalConditionList(params);
      if (result?.data?.data?.specialMedicalConditionColl) {
        MedicalConditionCollection =
          result.data.data.specialMedicalConditionColl.map((item: any) => ({
            ...item,
            label: item.medicalConditionName,
            value: item.specialMedicalConditionId?.toString(),
          }));
        if (oninit) setSpecialMedicalConditionColl(MedicalConditionCollection);
      }
      return MedicalConditionCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER MEDICAL CONDITION
  async function filterMedicalCondition(inputValue: string) {
    let result = await fetchMedicalCondition(inputValue, false);
    return result;
  }

  // MEDICAL CONDITION NAME LOAD ON SEARCH
  const loadMedicalConditionList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    const existingMedicalConditionNames = new Set(
      specialMedicalConditionCollWatch
        ?.filter((item: any) => item?.mode !== "D")
        ?.map((item: any) => item.medicalConditionName),
    );

    if (inputValue && inputValue.length > 0) {
      filterMedicalCondition(inputValue?.trim()).then((r) => {
        const uniqueResults = r?.filter(
          (medicalCondition: any) =>
            !existingMedicalConditionNames.has(medicalCondition.label),
        );
        callback(uniqueResults);
      });
    } else {
      const uniqueDefaultOptions = (specialMedicalConditionColl || []).filter(
        (medicalCondition: any) =>
          !existingMedicalConditionNames.has(medicalCondition.label),
      );
      callback(uniqueDefaultOptions);
    }
  };

  useEffect(() => {
    if (param.tabType === 2) {
      getVaccineTemplateList();
      getPatientVaccineModuleList();
    }
  }, [param.tabType]);

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    setIsLoading(true);
    await savePatient(data);
    setIsLoading(false);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      const languages: any = await getLanguageList();
      //..await fetchDepartment();
      await fetchCountry();
      await fetchMedicalCondition("", true);
      if (curPatientId > 0) {
        const Patient = await getPatientDetailById();
        //await fetchSpeciality(Patient?.departmentId);
        if (Patient?.countryId) {
          await fetchCountryRegionData(Patient?.countryId);
        }
      } else {
        const PatientNumber: any = await getPatientNumber();
        await fetchCountryRegionData(methods.getValues()?.countryId?.value);
        const preferredLanguage: any = languages?.find(
          (x) =>
            x?.languageCode === PreferredDefaultLanguageConfig?.configValue,
        );
        methods.reset({
          ...initPatientData,
          patientNumber: PatientNumber
            ? PatientNumber?.prefix + PatientNumber?.autoNumber
            : "",
          patientAutoNumber: PatientNumber?.autoNumber || 0,
          preferredLanguage:
            PreferredDefaultLanguageConfig?.configValue && preferredLanguage
              ? {
                  label: preferredLanguage?.languageName || "",
                  value: PreferredDefaultLanguageConfig?.configValue || "",
                }
              : null,
          preferredLanguageName: preferredLanguage?.languageName || "",
        });
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curPatientId]);

  const patientFirstNameRef = React.useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isLoading && isForPopUp) {
      setTimeout(() => {
        patientFirstNameRef.current?.focus();
        patientFirstNameRef.current?.select();
      }, 200);
    }
  }, [isLoading, customPopUp.open]);

  const handleYearChange = (value: number) => {
    const months = methods.getValues("ageMonth");
    const days = methods.getValues("ageDay");

    updateDateOfBirth(value, months, days);
  };

  const handleMonthChange = (value: number) => {
    const years = methods.getValues("ageYear");
    const days = methods.getValues("ageDay");

    updateDateOfBirth(years, value, days);
  };

  const handleDayChange = (value: number) => {
    const years = methods.getValues("ageYear");
    const months = methods.getValues("ageMonth");

    updateDateOfBirth(years, months, value);
  };

  // const updateDateOfBirth = (years: number, months: number, days: number) => {
  //   let today = new Date();

  //   let dob = new Date(
  //     today.getFullYear() - years,
  //     today.getMonth() - months,
  //     today.getDate() - days
  //   );

  //   if (dob.getMonth() < 0) {
  //     dob.setFullYear(dob.getFullYear() - 1);
  //     dob.setMonth(dob.getMonth() + 12);
  //   }

  //   while (dob.getDate() <= 0) {
  //     dob.setMonth(dob.getMonth() - 1);
  //     dob.setDate(
  //       dob.getDate() +
  //       new Date(dob.getFullYear(), dob.getMonth() + 1, 0).getDate()
  //     );
  //   }

  //   let formattedDate = format(dob, "yyyy-MM-dd");

  //   methods.setValue("dateOfBirth", formattedDate, {
  //     shouldValidate: true,
  //   });

  //   methods.setValue("ageYear", years);
  //   methods.setValue("ageMonth", months);
  //   methods.setValue("ageDay", days);
  // };
  const updateDateOfBirth = (years: number, months: number, days: number) => {
    let today = dayjs();

    let dob = today
      .subtract(years, "year")
      .subtract(days, "day") // ? pehle days
      .subtract(months, "month"); // ? phir months

    let formattedDate = dob.format("YYYY-MM-DD");

    methods.setValue("dateOfBirth", formattedDate, {
      shouldValidate: true,
    });

    methods.setValue("ageYear", years);
    methods.setValue("ageMonth", months);
    methods.setValue("ageDay", days);
  };
  return (
    <>
      <Box>
        <Box className="main-container" pl={0}>
          {isForPopUp === false && (
            <>
              <Grid container className="page-header" sx={{ mb: 0 }}>
                <Grid item xs={6} sm={6} md={6}>
                  <Box
                    sx={{
                      display: "flex",
                      height: "100%",
                      alignItems: "center",
                    }}
                  >
                    <CustomBreadcrumbs />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={6} md={6}>
                  <Box className="page-header-icon">
                    <Button
                      color="primary"
                      variant="outlined"
                      type="button"
                      className="secondary-button"
                      startIcon={<ArrowBackIcon />}
                      onClick={handleBack}
                    >
                      Back
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} action="">
              <Grid container>
                <Grid item xs={12} sx={{ mt: 0 }}>
                  <Tabs
                    value={param.tabType}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    indicatorColor="primary"
                    aria-label="doctor"
                    TabIndicatorProps={{ sx: { backgroundColor: " #2e67a3" } }}
                    sx={{
                      maxWidth: "100%",
                      "& button:hover": {
                        backgroundColor: "var(--opd-hover-bg, #f1f6fe)",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      },
                    }}
                  >
                    <Tab
                      sx={{ textTransform: "capitalize !important" }}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>General Information</span>
                        </Box>
                      }
                    />
                    <Tab
                      sx={{ textTransform: "capitalize !important" }}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>Special Medical Condition</span>
                        </Box>
                      }
                    />
                    {(!showDental || isSuperAdmin) && curPatientId && (
                      <Tab
                        sx={{ textTransform: "capitalize !important" }}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span>Patient Vaccination</span>
                          </Box>
                        }
                      />
                    )}
                    <Tab
                      sx={{ textTransform: "capitalize !important" }}
                      value={3}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>Report Parameter</span>
                        </Box>
                      }
                    />
                  </Tabs>
                </Grid>
                {param.tabType === 0 && (
                  <Grid container gap={2} sx={{ mt: 2 }}>
                    {/* <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Speciality</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.departmentId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                      <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              isDisabled={curPatientId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });
                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchSpeciality(e.value);                                  
                                    setIsLoading(false);
                                  } else {
                                    setspecialityData([]);                                    
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.specialityId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                       <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              isDisabled={curPatientId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.specialityId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "specialityId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );    
                                  methods.setValue("consultingDoctorId", null, {
                                    shouldValidate: true,
                                  });       
                                  methods.setValue("referralDoctorId", null, {
                                    shouldValidate: true,
                                  });                                              
                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchConsultingDoctor("", true);
                                    await fetchReferralDoctor("", true);
                                    setIsLoading(false);
                                  } else {
                                    setdoctorConsultingData([]);
                                  }
                                }
                              }}

                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid> */}
                    <Grid item xs={12} className={"group-container"}>
                      <Grid item container xs={12} spacing={2}>
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography variant="h6">
                              Basic Information
                            </Typography>
                            {/* <Typography
                              component="button"
                              type="button"
                              onClick={() => {
                                const pid =
                                  Number(curPatientId) ||
                                  Number(methods.getValues("patientId")) ||
                                  0;
                                if (!pid) {
                                  updateUserContext({
                                    ...userContext,
                                    isAlert: true,
                                    alertMessage:
                                      "Save the patient first, then open ABHA Verification.",
                                    alertType: AlertType.Info,
                                  });
                                  return;
                                }
                                navigate(
                                  `/abha?tab=m1&patientId=${encodeURIComponent(
                                    String(pid),
                                  )}`,
                                );
                              }}
                              sx={{
                                border: 0,
                                background: "none",
                                cursor: "pointer",
                                color: "primary.main",
                                fontWeight: 600,
                                fontSize: "0.95rem",
                                textDecoration: "underline",
                                p: 0,
                                "&:hover": { opacity: 0.85 },
                              }}
                            >
                              ABHA Verification
                            </Typography> */}
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.patientNumber
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              EMR ID *
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <CustomTextField
                                placeholder=""
                                autoCapitalize="none"
                                disabled={true}
                                type="text"
                                fullWidth
                                size="small"
                                {...methods.register("patientNumber")}
                                error={!!methods.formState.errors.patientId}
                                className={`text-input ${
                                  methods.formState.errors.patientNumber
                                    ? "error"
                                    : ""
                                }`}
                              />
                            </SkeletonWrapper>
                          </FormControl>
                          <Typography className="validation-message">
                            {methods.formState.errors.patientNumber?.message}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={9} />
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.prefix
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Prefix
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <Controller
                                control={methods.control}
                                name="prefix"
                                render={({ field }) => (
                                  <CustomSelect
                                    {...field}
                                    isNotCreatable={true}
                                    id={"prefix"}
                                    FieldName={"prefix"}
                                    options={prefixOptions}
                                    openMenuOnFocus
                                    isClearable
                                    isSearchable
                                    menuPortalTarget={document.querySelector(
                                      "body",
                                    )}
                                    handleChange={async (e) => {
                                      if (
                                        methods
                                          ?.getValues()
                                          ?.prefix?.value?.toString() !==
                                        e?.value?.toString()
                                      ) {
                                        methods.setValue(
                                          "prefix",
                                          e?.value ? e : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                        methods.setValue(
                                          "prefixName",
                                          e?.value ? e?.label : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                      }
                                    }}
                                  />
                                )}
                              />
                            </SkeletonWrapper>
                            <Typography className="validation-message">
                              {methods.formState.errors.prefix?.message}
                            </Typography>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.firstName
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              First Name *
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <CustomTextField
                                inputRef={patientFirstNameRef}
                                placeholder=""
                                autoCapitalize="none"
                                type="text"
                                fullWidth
                                size="small"
                                {...methods.register("firstName", {
                                  onChange: (e) => {
                                    const input = e.target;
                                    const selectionStart = input.selectionStart;
                                    const selectionEnd = input.selectionEnd;

                                    // CONVERT INPUT INTO UPPERCASE LATTERS
                                    e.target.value = input.value
                                      ? input.value.toUpperCase()
                                      : "";

                                    // Restore the cursor position
                                    setTimeout(() => {
                                      input.setSelectionRange(
                                        selectionStart,
                                        selectionEnd,
                                      );
                                    });

                                    methods.setValue(
                                      "firstName",
                                      e.target.value,
                                      {
                                        shouldValidate: true,
                                      },
                                    ); // UPDATE VALUE
                                  },
                                })}
                                error={!!methods.formState.errors.firstName}
                                className={`text-input ${
                                  methods.formState.errors.firstName
                                    ? "error"
                                    : ""
                                }`}
                              />
                            </SkeletonWrapper>
                          </FormControl>
                          <Typography className="validation-message">
                            {methods.formState.errors.firstName?.message}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.middleName
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Middle Name
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <CustomTextField
                                placeholder=""
                                autoCapitalize="none"
                                type="text"
                                fullWidth
                                size="small"
                                {...methods.register("middleName", {
                                  onChange: (e) => {
                                    const input = e.target;
                                    const selectionStart = input.selectionStart;
                                    const selectionEnd = input.selectionEnd;

                                    // CONVERT INPUT INTO UPPERCASE LATTERS
                                    e.target.value = input.value
                                      ? input.value.toUpperCase()
                                      : "";

                                    // Restore the cursor position
                                    setTimeout(() => {
                                      input.setSelectionRange(
                                        selectionStart,
                                        selectionEnd,
                                      );
                                    });

                                    methods.setValue(
                                      "middleName",
                                      e.target.value,
                                      {
                                        shouldValidate: true,
                                      },
                                    ); // UPDATE VALUE
                                  },
                                })}
                                error={!!methods.formState.errors.middleName}
                                className={`text-input ${
                                  methods.formState.errors.middleName
                                    ? "error"
                                    : ""
                                }`}
                              />
                            </SkeletonWrapper>
                          </FormControl>
                          <Typography className="validation-message">
                            {methods.formState.errors.middleName?.message}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.lastName
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Last Name *
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <CustomTextField
                                placeholder=""
                                autoCapitalize="none"
                                type="text"
                                fullWidth
                                size="small"
                                {...methods.register("lastName", {
                                  onChange: (e) => {
                                    const input = e.target;
                                    const selectionStart = input.selectionStart;
                                    const selectionEnd = input.selectionEnd;

                                    // CONVERT INPUT INTO UPPERCASE LATTERS
                                    e.target.value = input.value
                                      ? input.value.toUpperCase()
                                      : "";

                                    // Restore the cursor position
                                    setTimeout(() => {
                                      input.setSelectionRange(
                                        selectionStart,
                                        selectionEnd,
                                      );
                                    });

                                    methods.setValue(
                                      "lastName",
                                      e.target.value,
                                      {
                                        shouldValidate: true,
                                      },
                                    ); // UPDATE VALUE
                                  },
                                })}
                                error={!!methods.formState.errors.lastName}
                                className={`text-input ${
                                  methods.formState.errors.lastName
                                    ? "error"
                                    : ""
                                }`}
                              />
                            </SkeletonWrapper>
                          </FormControl>
                          <Typography className="validation-message">
                            {methods.formState.errors.lastName?.message}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.dateOfBirth
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Date of Birth *
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <DatePickerComponent
                                format="DD-MM-YYYY"
                                mode="date"
                                slotProps={{ textField: { size: "small" } }}
                                value={
                                  methods?.getValues()?.dateOfBirth
                                    ? dayjs(methods?.getValues()?.dateOfBirth)
                                    : null
                                }
                                maxDate={dayjs()}
                                // prithavi working on this
                                maxDate={dayjs()}
                                onChange={(e: any) => {
                                  if (e !== null && isValid(e.$d)) {
                                    let birthDate = new Date(e.$d);

                                    let { Year, Month, Day } =
                                      getAge(birthDate);

                                    // let Year =
                                    //   today.getFullYear() - birthDate.getFullYear();
                                    // let Month =
                                    //   today.getMonth() - birthDate.getMonth();
                                    // let Day = today.getDate() - birthDate.getDate();

                                    // if (Day < 0) {
                                    //   Month -= 1;
                                    //   Day += new Date(
                                    //     today.getFullYear(),
                                    //     today.getMonth(),
                                    //     0
                                    //   ).getDate();
                                    // }

                                    // if (Month < 0) {
                                    //   Year -= 1;
                                    //   Month += 12;
                                    // }
                                    // if (isAfter(birthDate, today)) {
                                    //   methods.setValue("ageYear", 0);
                                    //   methods.setValue("ageMonth", 0);
                                    //   methods.setValue("ageDay", 0);
                                    //   return;
                                    // }

                                    let savedDateFormate = format(
                                      e.$d,
                                      "yyyy-MM-dd",
                                    );
                                    methods.setValue(
                                      "dateOfBirth",
                                      savedDateFormate,
                                    );
                                    methods.setValue("ageYear", Year);
                                    methods.setValue("ageMonth", Month);
                                    methods.setValue("ageDay", Day);
                                  } else {
                                    methods.setValue("dateOfBirth", "");
                                  }
                                }}
                              />
                            </SkeletonWrapper>
                            <Typography className="validation-message">
                              {methods.formState.errors.dateOfBirth?.message}
                            </Typography>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.ageYear
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Age *
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <Grid container gap={1}>
                                <Grid item xs={3} sm={3} md={3}>
                                  <CustomTextField
                                    placeholder=""
                                    autoCapitalize="none"
                                    type="text"
                                    fullWidth
                                    size="small"
                                    {...methods.register("ageYear", {
                                      onChange: (e: any) =>
                                        handleYearChange(
                                          parseInt(e.target.value, 10) || 0,
                                        ),
                                    })}
                                    error={!!methods.formState.errors.ageYear}
                                    className={`text-input `}
                                    inputProps={{
                                      maxLength: 2,
                                    }}
                                    InputProps={{
                                      onKeyPress: (event) => {
                                        if (!/[0-9]/.test(event.key)) {
                                          event.preventDefault();
                                        }
                                      },
                                      endAdornment: (
                                        <span style={{ color: "grey" }}>Y</span>
                                      ),
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={3} sm={3} md={3}>
                                  <CustomTextField
                                    placeholder=""
                                    autoCapitalize="none"
                                    type="text"
                                    fullWidth
                                    size="small"
                                    {...methods.register("ageMonth", {
                                      onChange: (e: any) =>
                                        handleMonthChange(
                                          parseInt(e.target.value, 10) || 0,
                                        ),
                                    })}
                                    error={!!methods.formState.errors.ageMonth}
                                    className={`text-input `}
                                    inputProps={{
                                      maxLength: 2,
                                    }}
                                    InputProps={{
                                      onKeyPress: (event) => {
                                        if (!/[0-9]/.test(event.key)) {
                                          event.preventDefault();
                                        }
                                      },
                                      endAdornment: (
                                        <span style={{ color: "grey" }}>M</span>
                                      ),
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={3} sm={3} md={3}>
                                  <CustomTextField
                                    placeholder=""
                                    autoCapitalize="none"
                                    type="text"
                                    fullWidth
                                    size="small"
                                    {...methods.register("ageDay", {
                                      onChange: (e: any) =>
                                        handleDayChange(
                                          parseInt(e.target.value, 10) || 0,
                                        ),
                                    })}
                                    error={!!methods.formState.errors.ageDay}
                                    className={`text-input `}
                                    inputProps={{
                                      maxLength: 2,
                                    }}
                                    InputProps={{
                                      onKeyPress: (event) => {
                                        if (!/[0-9]/.test(event.key)) {
                                          event.preventDefault();
                                        }
                                      },
                                      endAdornment: (
                                        <span style={{ color: "grey" }}>D</span>
                                      ),
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </SkeletonWrapper>
                          </FormControl>
                          <Typography className="validation-message">
                            {methods.formState.errors.ageYear?.message}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.gender
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Gender *
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <Controller
                                control={methods.control}
                                name="gender"
                                render={({ field }) => (
                                  <CustomSelect
                                    {...field}
                                    isNotCreatable={true}
                                    id={"gender"}
                                    FieldName={"gender"}
                                    options={genderOptions}
                                    openMenuOnFocus
                                    isClearable
                                    isSearchable
                                    menuPortalTarget={document.querySelector(
                                      "body",
                                    )}
                                    handleChange={async (e) => {
                                      if (
                                        methods
                                          ?.getValues()
                                          ?.gender?.value?.toString() !==
                                        e?.value?.toString()
                                      ) {
                                        methods.setValue(
                                          "gender",
                                          e?.value ? e : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                        methods.setValue(
                                          "genderName",
                                          e?.value ? e?.label : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                      }
                                    }}
                                  />
                                )}
                              />
                            </SkeletonWrapper>
                            <Typography className="validation-message">
                              {methods.formState.errors.gender?.message}
                            </Typography>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.preferredLanguage
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Preferred Language
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <Controller
                                control={methods.control}
                                name="preferredLanguage"
                                render={({ field }) => (
                                  <CustomSelect
                                    {...field}
                                    isNotCreatable={true}
                                    id={"preferredLanguage"}
                                    FieldName={"preferredLanguage"}
                                    options={resultDataCollection}
                                    openMenuOnFocus
                                    isClearable
                                    isSearchable
                                    menuPortalTarget={document.querySelector(
                                      "body",
                                    )}
                                    handleChange={async (e) => {
                                      if (
                                        methods
                                          ?.getValues()
                                          ?.preferredLanguage?.value?.toString() !==
                                        e?.value?.toString()
                                      ) {
                                        methods.setValue(
                                          "preferredLanguage",
                                          e?.value ? e : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                        methods.setValue(
                                          "preferredLanguageName",
                                          e?.value ? e?.label : null,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                      }
                                    }}
                                  />
                                )}
                              />
                            </SkeletonWrapper>
                            <Typography className="validation-message">
                              {
                                methods.formState.errors.preferredLanguage
                                  ?.message
                              }
                            </Typography>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <div className="input-group">
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.primaryMobileNumber
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Primary Mobile Number (whatsapp){" "}
                              <CustomTooltip
                                title="This field is important to be filled for better communication"
                                hideHeader={true}
                              ></CustomTooltip>
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <PhoneInput
                                id="primaryMobileNumber"
                                international
                                countryCallingCodeEditable={false}
                                defaultCountry="IN"
                                {...methods.register("primaryMobileNumber")}
                                error={
                                  methods.formState.errors.primaryMobileNumber
                                    ?.message
                                }
                                className={`phone-input ${
                                  methods.formState.errors.primaryMobileNumber
                                    ? "error"
                                    : ""
                                }`}
                                onChange={(e) => {
                                  methods.setValue(
                                    "primaryMobileNumber",
                                    e || "",
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                                value={methods.getValues()?.primaryMobileNumber}
                                placeholder="Enter phone number"
                              />
                            </SkeletonWrapper>
                            <Typography className="validation-message">
                              {
                                methods.formState.errors.primaryMobileNumber
                                  ?.message
                              }
                            </Typography>
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.location
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Location / Area
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <CustomTextField
                                placeholder=""
                                autoCapitalize="none"
                                type="text"
                                fullWidth
                                size="small"
                                {...methods.register("location")}
                                error={!!methods.formState.errors.location}
                                className={`text-input ${
                                  methods.formState.errors.location
                                    ? "error"
                                    : ""
                                }`}
                              />
                            </SkeletonWrapper>
                          </FormControl>
                          <Typography className="validation-message">
                            {methods.formState.errors.location?.message}
                          </Typography>
                        </Grid>
                        {/* <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.maritalStatus
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Marital Status
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="maritalStatus"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"maritalStatus"}
                              FieldName={"maritalStatus"}
                              options={maritalStatusOptions}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.maritalStatus?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "maritalStatus",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue(
                                    "maritalStatusName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.maritalStatus?.message}
                      </Typography>
                    </FormControl>
                  </Grid> */}
                      </Grid>
                    </Grid>
                    <Grid item xs={12} className={"group-container"}>
                      <Typography
                        className="font-size-16 text-link"
                        onClick={(e) => {
                          setSeeMore(!seeMore);
                        }}
                      >
                        {seeMore ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Typography>See Less</Typography>
                            <ExpandLess />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Typography>See More</Typography>
                            <ExpandMore />
                          </Box>
                        )}
                      </Typography>
                    </Grid>
                    {seeMore && (
                      <>
                        <Grid item xs={12} className={"group-container"}>
                          <Grid item container spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="h6">
                                Contact Information
                              </Typography>
                            </Grid>
                            {/* <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${methods.formState.errors.location
                                ? "validation-message"
                                : ""
                              }`}
                          >
                            Location / Area
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("location")}
                              error={!!methods.formState.errors.location}
                              className={`text-input ${methods.formState.errors.location ? "error" : ""
                                }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.location?.message}
                        </Typography>
                      </Grid> */}
                            {/* <Grid item sm={6}></Grid> */}
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.address1
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Address 1{" "}
                                  <CustomTooltip
                                    title="This field is important to be filled for better communication"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder=""
                                    autoCapitalize="none"
                                    type="text"
                                    fullWidth
                                    size="small"
                                    {...methods.register("address1")}
                                    error={!!methods.formState.errors.address1}
                                    className={`text-input ${
                                      methods.formState.errors.address1
                                        ? "error"
                                        : ""
                                    }`}
                                  />
                                </SkeletonWrapper>
                              </FormControl>
                              <Typography className="validation-message">
                                {methods.formState.errors.address1?.message}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.address2
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Address 2{" "}
                                  <CustomTooltip
                                    title="This field is important to be filled for better communication"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder=""
                                    autoCapitalize="none"
                                    type="text"
                                    fullWidth
                                    size="small"
                                    {...methods.register("address2")}
                                    error={!!methods.formState.errors.address2}
                                    className={`text-input ${
                                      methods.formState.errors.address2
                                        ? "error"
                                        : ""
                                    }`}
                                  />
                                </SkeletonWrapper>
                              </FormControl>
                              <Typography className="validation-message">
                                {methods.formState.errors.address2?.message}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.city
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  City{" "}
                                  <CustomTooltip
                                    title="This field is important to be filled for better communication"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder=""
                                    autoCapitalize="none"
                                    type="text"
                                    fullWidth
                                    size="small"
                                    {...methods.register("city")}
                                    error={!!methods.formState.errors.city}
                                    className={`text-input ${
                                      methods.formState.errors.city
                                        ? "error"
                                        : ""
                                    }`}
                                  />
                                </SkeletonWrapper>
                              </FormControl>
                              <Typography className="validation-message">
                                {methods.formState.errors.city?.message}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.postalCode
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Postal Code
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder=""
                                    autoCapitalize="none"
                                    type="text"
                                    fullWidth
                                    id="postalCode"
                                    size="small"
                                    {...methods.register("postalCode")}
                                    error={
                                      !!methods.formState.errors.postalCode
                                    }
                                    className={`text-input ${
                                      methods.formState.errors.postalCode
                                        ? "error"
                                        : ""
                                    }`}
                                  />
                                </SkeletonWrapper>
                              </FormControl>
                              <Typography className="validation-message">
                                {methods.formState.errors.postalCode?.message}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.countryId
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Country *
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <Controller
                                    control={methods.control}
                                    name="countryId"
                                    render={({ field }) => (
                                      <CustomSelect
                                        {...field}
                                        isNotCreatable={true}
                                        id={"countryId"}
                                        FieldName={"countryId"}
                                        options={countryData}
                                        openMenuOnFocus
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={document.querySelector(
                                          "body",
                                        )}
                                        handleChange={async (e) => {
                                          if (
                                            methods
                                              ?.getValues()
                                              ?.countryId?.value?.toString() !==
                                            e?.value?.toString()
                                          ) {
                                            methods.setValue(
                                              "countryId",
                                              e?.value ? e : null,
                                              {
                                                shouldValidate: true,
                                              },
                                            );
                                            methods.setValue(
                                              "countryName",
                                              e?.value ? e?.label : null,
                                              {
                                                shouldValidate: true,
                                              },
                                            );
                                            methods.setValue(
                                              "countryRegionId",
                                              null,
                                              {
                                                shouldValidate: true,
                                              },
                                            );
                                            methods.setValue(
                                              "countryRegionName",
                                              "",
                                              {
                                                shouldValidate: true,
                                              },
                                            );
                                            if (e?.value !== null) {
                                              setIsLoading(true);
                                              await fetchCountryRegionData(
                                                e?.value,
                                              );
                                              setIsLoading(false);
                                            } else {
                                              setCountryRegionData([]);
                                            }
                                          }
                                        }}
                                        className={`text-input ${
                                          methods.formState.errors.postalCode
                                            ? "error"
                                            : ""
                                        }`}
                                      />
                                    )}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {methods.formState.errors.countryId?.message}
                                </Typography>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.countryRegionId
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  State *
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <Controller
                                    control={methods.control}
                                    name="countryRegionId"
                                    render={({ field }) => (
                                      <CustomSelect
                                        {...field}
                                        isNotCreatable={true}
                                        id={"countryRegionId"}
                                        FieldName={"countryRegionId"}
                                        options={countryRegionData}
                                        openMenuOnFocus
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={document.querySelector(
                                          "body",
                                        )}
                                        handleChange={(e) => {
                                          methods.setValue(
                                            "countryRegionId",
                                            e?.value ? e : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          methods.setValue(
                                            "countryRegionName",
                                            e?.value ? e?.label : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                        }}
                                        className={`text-input ${
                                          methods.formState.errors.countryId
                                            ? "error"
                                            : ""
                                        }`}
                                      />
                                    )}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {
                                    methods.formState.errors.countryRegionId
                                      ?.message
                                  }
                                </Typography>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.email
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Email{" "}
                                  <CustomTooltip
                                    title="This field is important to be filled for better communication"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder=""
                                    autoCapitalize="none"
                                    type="text"
                                    fullWidth
                                    size="small"
                                    {...methods.register("email")}
                                    error={!!methods.formState.errors.email}
                                    className={`text-input ${
                                      methods.formState.errors.email
                                        ? "error"
                                        : ""
                                    }`}
                                  />
                                </SkeletonWrapper>
                              </FormControl>
                              <Typography className="validation-message">
                                {methods.formState.errors.email?.message}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <div className="input-group">
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors
                                      .secondaryMobileNumber
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Secondary Mobile Number{" "}
                                  <CustomTooltip
                                    title="This field is important to be filled for better communication"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <PhoneInput
                                    id="secondaryMobileNumber"
                                    international
                                    countryCallingCodeEditable={false}
                                    defaultCountry="IN"
                                    {...methods.register(
                                      "secondaryMobileNumber",
                                    )}
                                    error={
                                      methods.formState.errors
                                        .secondaryMobileNumber?.message
                                    }
                                    className={`phone-input ${
                                      methods.formState.errors
                                        .secondaryMobileNumber
                                        ? "error"
                                        : ""
                                    }`}
                                    value={
                                      methods.getValues()?.secondaryMobileNumber
                                    }
                                    onChange={(e) => {
                                      methods.setValue(
                                        "secondaryMobileNumber",
                                        e || "",
                                      );
                                    }}
                                    placeholder="Enter phone number"
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {
                                    methods.formState.errors
                                      .secondaryMobileNumber?.message
                                  }
                                </Typography>
                              </div>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} className={"group-container"}>
                          <Grid item container spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="h6">
                                Other Information
                              </Typography>
                            </Grid>

                            {/* <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.consultingDoctorId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Consulting Doctor *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="consultingDoctorId"
                          render={({ field }) => (                          
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              placeholder="Select Consulting  Doctor"
                              id={"consultingDoctorId"}
                              FieldName={"consultingDoctorId"}
                              defaultOptions={doctorConsultingData}
                              openMenuOnFocus
                              isDisabled={!methods.getValues("departmentId") || !methods.getValues("specialityId")}                              
                              isClearable
                              isSearchable
                              loadOptions={loadConsultingDoctorList}
                              menuPortalTarget={document.querySelector("body")}
                              className={`text-input`}
                              handleChange={(e) => {
                                if(e){
                                  if (e.__isNew__ === true) {
                                    handleClickOpen(true, e.label, { value: 'CONSULTING_DOCTOR', label: 'Consuliting Doctor' },);
                                    }
                                  } 

                                if (
                                        methods
                                          ?.getValues()
                                          ?.consultingDoctorId?.value?.toString() !==
                                        e?.value?.toString()
                                      ) {
                                        methods.setValue(
                                          "consultingDoctorId",
                                          e?.value ? e : null,
                                          {
                                            shouldValidate: true,
                                          }
                                        );
                                      }
                                    }}                                 
                            />
                        )}
                  />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.consultingDoctorId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.referralDoctorId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Referred by 
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                      <Controller
                          control={methods.control}
                          name="referralDoctorId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                            {...field}
                            isNotCreatable={false}
                            placeholder="Select Referral Doctor"
                            id={"referralDoctorId"}
                            FieldName={"referralDoctorId"}
                            defaultOptions={doctorReferralData}
                            isDisabled={!methods.getValues("departmentId") || !methods.getValues("specialityId")} 
                            openMenuOnFocus
                            isClearable
                            isSearchable
                            loadOptions={loadReferralDoctorList}
                            menuPortalTarget={document.querySelector("body")}
                            className={`text-input`}
                            handleChange={(e) => {
                              if(e){
                                if (e.__isNew__ === true) {
                                  handleClickOpen(true, e.label, { value: 'REFERRAL_DOCTOR', label: 'Referral Doctor' });
                                  }
                                } 
                              if (
                                      methods
                                        ?.getValues()
                                        ?.referralDoctorId?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "referralDoctorId",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        }
                                      );
                                    }
                                  }}                                 
                          />
                          )}
                        />
                        
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.referralDoctorId?.message}
                      </Typography>
                    </FormControl>
                  </Grid> */}
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.maritalStatus
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Marital Status
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <Controller
                                    control={methods.control}
                                    name="maritalStatus"
                                    render={({ field }) => (
                                      <CustomSelect
                                        {...field}
                                        isNotCreatable={true}
                                        id={"maritalStatus"}
                                        FieldName={"maritalStatus"}
                                        options={maritalStatusOptions}
                                        openMenuOnFocus
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={document.querySelector(
                                          "body",
                                        )}
                                        handleChange={async (e) => {
                                          if (
                                            methods
                                              ?.getValues()
                                              ?.maritalStatus?.value?.toString() !==
                                            e?.value?.toString()
                                          ) {
                                            methods.setValue(
                                              "maritalStatus",
                                              e?.value ? e : null,
                                              {
                                                shouldValidate: true,
                                              },
                                            );
                                            methods.setValue(
                                              "maritalStatusName",
                                              e?.value ? e?.label : null,
                                              {
                                                shouldValidate: true,
                                              },
                                            );
                                          }
                                        }}
                                      />
                                    )}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {
                                    methods.formState.errors.maritalStatus
                                      ?.message
                                  }
                                </Typography>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.bloodGroup
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Blood Group
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <Controller
                                    control={methods.control}
                                    name="bloodGroup"
                                    render={({ field }) => (
                                      <CustomSelect
                                        {...field}
                                        isNotCreatable={true}
                                        id={"bloodGroup"}
                                        FieldName={"bloodGroup"}
                                        options={bloodgroupOptions}
                                        openMenuOnFocus
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={document.querySelector(
                                          "body",
                                        )}
                                        handleChange={async (e) => {
                                          if (
                                            methods
                                              ?.getValues()
                                              ?.bloodGroup?.value?.toString() !==
                                            e?.value?.toString()
                                          ) {
                                            methods.setValue(
                                              "bloodGroup",
                                              e?.value ? e : null,
                                              {
                                                shouldValidate: true,
                                              },
                                            );
                                            methods.setValue(
                                              "bloodGroupName",
                                              e?.value ? e?.label : null,
                                              {
                                                shouldValidate: true,
                                              },
                                            );
                                          }
                                        }}
                                      />
                                    )}
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {methods.formState.errors.bloodGroup?.message}
                                </Typography>
                              </FormControl>
                            </Grid>
                            {/* BIRTH WEIGHT */}
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.birthWeight
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Birth Weight{" "}
                                  <CustomTooltip
                                    title="This field is important for automated dose calculation"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="000.000"
                                    fullWidth
                                    id="birthWeight"
                                    size="small"
                                    disabled={false}
                                    inputProps={{
                                      ...weightProps,
                                    }}
                                    {...methods.register("birthWeight")}
                                    onFocus={handleFocus}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          Kg
                                        </InputAdornment>
                                      ),
                                    }}
                                    error={
                                      !!methods.formState.errors.birthWeight
                                    }
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {
                                    methods.formState.errors.birthWeight
                                      ?.message
                                  }
                                </Typography>
                              </FormControl>
                            </Grid>
                            {/* MOTHER'S HEIGHT */}
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.motherHeight
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Mother's Height{" "}
                                  <CustomTooltip
                                    title="This field is important for automated Chart calculation"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="000.0"
                                    fullWidth
                                    id="motherHeight"
                                    size="small"
                                    inputProps={{
                                      ...heightProps,
                                    }}
                                    {...methods.register("motherHeight")}
                                    onFocus={handleFocus}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          cm
                                        </InputAdornment>
                                      ),
                                    }}
                                    error={
                                      !!methods.formState.errors.motherHeight
                                    }
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {
                                    methods.formState.errors.motherHeight
                                      ?.message
                                  }
                                </Typography>
                              </FormControl>
                            </Grid>

                            {/* FATHER'S HEIGHT */}
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.fatherHeight
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Father's Height{" "}
                                  <CustomTooltip
                                    title="This field is important for automated Chart calculation"
                                    hideHeader={true}
                                  ></CustomTooltip>
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder="000.0"
                                    fullWidth
                                    id="fatherHeight"
                                    size="small"
                                    inputProps={{
                                      ...heightProps,
                                    }}
                                    {...methods.register("fatherHeight")}
                                    onFocus={handleFocus}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          cm
                                        </InputAdornment>
                                      ),
                                    }}
                                    error={
                                      !!methods.formState.errors.fatherHeight
                                    }
                                  />
                                </SkeletonWrapper>
                                <Typography className="validation-message">
                                  {
                                    methods.formState.errors.fatherHeight
                                      ?.message
                                  }
                                </Typography>
                              </FormControl>
                            </Grid>

                            {/* <Grid item md={6}></Grid> */}
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <Box className="check-box">
                                  <FormControlLabel
                                    control={
                                      <SkeletonWrapper
                                        isLoading={isLoading}
                                        className="skeleton-check-box"
                                      >
                                        <Checkbox
                                          {...(methods.register("hasMediclaim"),
                                          {
                                            onChange: (e) => {
                                              methods.setValue(
                                                "hasMediclaim",
                                                e.target.checked,
                                                {
                                                  shouldValidate: true,
                                                },
                                              );
                                            },
                                          })}
                                          checked={
                                            methods.getValues()?.hasMediclaim ||
                                            false
                                          }
                                        />
                                      </SkeletonWrapper>
                                    }
                                    label="Has Mediclaim"
                                  />
                                </Box>
                                <Typography className="validation-message">
                                  {
                                    methods.formState.errors.hasMediclaim
                                      ?.message
                                  }
                                </Typography>
                              </FormControl>
                            </Grid>

                            {methods.getValues("hasMediclaim") && (
                              <>
                                <Grid item xs={12} sm={6} md={3}>
                                  <FormControl fullWidth>
                                    <FormLabel
                                      className={`fileds-label-text  ${
                                        methods.formState.errors
                                          .nameOfInsuranceCompany
                                          ? "validation-message"
                                          : ""
                                      }`}
                                    >
                                      Name of Insurance company
                                    </FormLabel>
                                    <SkeletonWrapper
                                      isLoading={isLoading}
                                      className="skeleton-input"
                                    >
                                      <CustomTextField
                                        placeholder=""
                                        autoCapitalize="none"
                                        type="text"
                                        fullWidth
                                        size="small"
                                        {...methods.register(
                                          "nameOfInsuranceCompany",
                                        )}
                                        error={
                                          !!methods.formState.errors
                                            .nameOfInsuranceCompany
                                        }
                                        className={`text-input ${
                                          methods.formState.errors
                                            .nameOfInsuranceCompany
                                            ? "error"
                                            : ""
                                        }`}
                                      />
                                    </SkeletonWrapper>
                                  </FormControl>
                                  <Typography className="validation-message">
                                    {
                                      methods.formState.errors
                                        .nameOfInsuranceCompany?.message
                                    }
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <FormControl fullWidth>
                                    <FormLabel
                                      className={`fileds-label-text  ${
                                        methods.formState.errors
                                          .annualIncomeOfPatientParent
                                          ? "validation-message"
                                          : ""
                                      }`}
                                    >
                                      Annual Income of Patient / Parent /
                                      Guardian
                                    </FormLabel>
                                    <SkeletonWrapper
                                      isLoading={isLoading}
                                      className="skeleton-input"
                                    >
                                      <CustomTextField
                                        placeholder=""
                                        autoCapitalize="none"
                                        type="text"
                                        fullWidth
                                        size="small"
                                        {...methods.register(
                                          "annualIncomeOfPatientParent",
                                        )}
                                        error={
                                          !!methods.formState.errors
                                            .annualIncomeOfPatientParent
                                        }
                                        className={`text-input ${
                                          methods.formState.errors
                                            .annualIncomeOfPatientParent
                                            ? "error"
                                            : ""
                                        }`}
                                      />
                                    </SkeletonWrapper>
                                  </FormControl>
                                  <Typography className="validation-message">
                                    {
                                      methods.formState.errors
                                        .annualIncomeOfPatientParent?.message
                                    }
                                  </Typography>
                                </Grid>
                              </>
                            )}
                            {(!showDental || isSuperAdmin) && (
                              <Grid item xs={12}>
                                <FormControl fullWidth>
                                  <Box className="check-box">
                                    <FormControlLabel
                                      control={
                                        <SkeletonWrapper
                                          isLoading={isLoading}
                                          className="skeleton-check-box"
                                        >
                                          <Checkbox
                                            checked={
                                              methods.watch(
                                                "enableVaccination",
                                              ) || false
                                            }
                                            disabled={
                                              vaccinationEnableMode !== "TRUE"
                                            }
                                            onChange={(e) =>
                                              methods.setValue(
                                                "enableVaccination",
                                                e.target.checked,
                                                {
                                                  shouldValidate: true,
                                                },
                                              )
                                            }
                                          />
                                        </SkeletonWrapper>
                                      }
                                      label="Enable Vaccination"
                                    />
                                  </Box>

                                  <Typography className="validation-message">
                                    {
                                      methods.formState.errors.enableVaccination
                                        ?.message
                                    }
                                  </Typography>
                                </FormControl>
                              </Grid>
                            )}
                            <Grid item xs={12} sm={6} md={12}>
                              <FormControl fullWidth>
                                <FormLabel
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.notes
                                      ? "validation-message"
                                      : ""
                                  }`}
                                >
                                  Notes
                                </FormLabel>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input"
                                >
                                  <CustomTextField
                                    placeholder=""
                                    autoCapitalize="none"
                                    type="text"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                    {...methods.register("notes")}
                                    error={!!methods.formState.errors.notes}
                                    className={`text-input ${
                                      methods.formState.errors.notes
                                        ? "error"
                                        : ""
                                    }`}
                                  />
                                </SkeletonWrapper>
                              </FormControl>
                              <Typography className="validation-message">
                                {methods.formState.errors.notes?.message}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}
                {param.tabType === 1 && (
                  <Grid item xs={12} className={"group-container"} mt={2}>
                    {isForPopUp && (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mb: 2,
                        }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<ArrowBackIcon />}
                          onClick={handleBack}
                          size="small"
                        >
                          Back
                        </Button>
                      </Grid>
                    )}
                    <Grid item container spacing={2}>
                      <Grid item xs={12}>
                        <TableContainer
                          component={Paper}
                          sx={{
                            maxHeight: "380px",
                            overflowY: "auto",
                          }}
                        >
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    borderBottom: "1px solid #ddd",
                                    width: "95%",
                                  }}
                                >
                                  Medical Condition Name
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    borderBottom: "1px solid #ddd",
                                    width: "5%",
                                  }}
                                />
                              </TableRow>
                            </TableHead>
                            {specialMedicalConditionCollWatch?.filter(
                              (i: any) => i?.mode !== "D",
                            )?.length > 0 ? (
                              <TableBody>
                                {specialMedicalConditionCollWatch
                                  ?.filter((item: any) => item?.mode !== "D")
                                  ?.map((item: any, index: number) => (
                                    <TableRow key={item?.displayOrder ?? index}>
                                      <TableCell
                                        sx={{
                                          fontWeight: 500,
                                          borderBottom: "1px solid #ddd",
                                          width: "95%",
                                        }}
                                      >
                                        <Typography>
                                          {item?.medicalConditionName ||
                                            item?.specialMedicalConditionId
                                              ?.label}
                                        </Typography>
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{
                                          borderBottom: "1px solid #ddd",
                                          width: "5%",
                                        }}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleDeleteSpecialMedicalCondition(
                                              item,
                                            )
                                          }
                                        >
                                          <CloseRoundedIcon
                                            sx={{
                                              color: "error.main",
                                              fontSize: 20,
                                            }}
                                          />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            ) : (
                              <Typography
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  mt: 2,
                                }}
                              >
                                No record(s) found
                              </Typography>
                            )}
                          </Table>
                        </TableContainer>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <SimpleAsyncSelect
                              placeholder="Search or create Medical Condition"
                              id={"specialMedicalConditionColl"}
                              FieldName={"specialMedicalConditionColl"}
                              key={asyncSelectKey?.medicalConditionSearch?.toString()}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                if (e) {
                                  if (e.__isNew__ === true) {
                                    handleClickOpen(
                                      true,
                                      e.label.toUpperCase(),
                                      "MEDICAL_CONDITION_NAME",
                                    );
                                  } else {
                                    await handleSpecialMedicalConditionDetailAdd(
                                      e,
                                    );
                                  }
                                }
                              }}
                              value={null}
                              loadOptions={loadMedicalConditionList}
                              defaultOptions={(
                                specialMedicalConditionColl || []
                              ).filter(
                                (medicalCondition: any) =>
                                  !new Set(
                                    specialMedicalConditionCollWatch
                                      ?.filter(
                                        (item: any) => item?.mode !== "D",
                                      )
                                      ?.map(
                                        (item: any) =>
                                          item.medicalConditionName,
                                      ),
                                  ).has(medicalCondition.label),
                              )}
                              isSearchable
                              isCreatable
                            />
                          </SkeletonWrapper>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                {param.tabType === 2 && (
                  // <Grid item xs={12} className={"group-container"}>
                  // <Grid item container xs={12} spacing={2}></Grid>
                  <Grid container spacing={2} mt={1}>
                    {patientVaccineModuleColl.length > 0 && (
                      <Grid item xs={12}>
                        {/* Vaccine Module List */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1, // space between items instead of mb on Paper
                          }}
                        >
                          {patientVaccineModuleColl.map((item, idx) => (
                            <Paper
                              key={item.patient_vaccine_module_id}
                              elevation={1}
                              sx={{
                                borderWidth: "2px",
                                borderRadius: "10px",
                                padding: "12px 12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                border: "1px solid black",
                                backgroundColor:
                                  item?.currentStatus?.toUpperCase() ===
                                  "COMPLETED"
                                    ? "#edf7ed"
                                    : item?.currentStatus?.toUpperCase() ===
                                        "UPCOMING"
                                      ? "white"
                                      : item?.currentStatus?.toUpperCase() ===
                                          "OVERDUE"
                                        ? "#fdeded"
                                        : item?.currentStatus?.toUpperCase() ===
                                            "NEXT DUE"
                                          ? "#fff4e5"
                                          : item?.currentStatus?.toUpperCase() ===
                                              "CURRENT DUE"
                                            ? "#fff4e5"
                                            : "#ffffff",
                              }}
                            >
                              <div
                                className="text-link"
                                onClick={() => handleEditModule(item)}
                                style={{ cursor: "pointer", flexGrow: 1 }}
                              >
                                {item.templateName}
                              </div>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    // fontWeight: 600,
                                    fontSize: "16px",
                                    color: "black",
                                    mr: 1,
                                  }}
                                >
                                  {item.upcomingVaccineDoseCount ?? 0}/
                                  {item.vaccineDoseDount ?? 0}
                                </Typography>

                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMenuOpen(e, item)}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Paper>
                          ))}
                        </Box>

                        {/* 3 Dot Menu */}
                        <Menu
                          anchorEl={menuAnchorEl}
                          open={Boolean(menuAnchorEl)}
                          onClose={handleMenuClose}
                        >
                          <MenuItem
                            onClick={() => {
                              handleMenuClose();
                              handleEditModule(selectedModule);
                            }}
                          >
                            Edit
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleMenuClose();
                              handleDeleteModule(selectedModule);
                            }}
                          >
                            Delete
                          </MenuItem>
                        </Menu>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenVaccineModuleDialog}
                            size="small"
                          >
                            Add Vaccine Module
                          </Button>
                        </Box>
                        {isForPopUp && (
                          <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            size="small"
                          >
                            Back
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                  // </Grid>
                )}
                {param.tabType === 0 && (
                  <Grid item xs={12} sx={{ mt: 3 }}>
                    <Box
                      sx={{ display: "flex", justifyContent: "end", gap: 2 }}
                    >
                      <LoadingButton
                        color="primary"
                        loadingPosition="start"
                        startIcon={<></>}
                        variant="contained"
                        type="submit"
                        className="primary-button"
                        name="save"
                        disabled={isLoading || isLoading}
                      >
                        {curPatientId > 0 ? "Update" : "Save"}
                      </LoadingButton>
                      <LoadingButton
                        color="primary"
                        loadingPosition="start"
                        startIcon={<></>}
                        variant="outlined"
                        type="button"
                        className="secondary-button"
                        name="cancel"
                        onClick={handleBack}
                      >
                        Cancel
                      </LoadingButton>
                    </Box>
                  </Grid>
                )}
                {param.tabType === 3 && (
                  <Grid item xs={12} className={"group-container"} mt={2}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: isForPopUp
                          ? "space-between"
                          : "flex-end",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenReportParameterDialog}
                        size="small"
                      >
                        Add
                      </Button>
                      {isForPopUp && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ArrowBackIcon />}
                          onClick={handleBack}
                        >
                          Back
                        </Button>
                      )}
                    </Box>
                    <Grid item xs={12} mt={2}>
                      <TableContainer>
                        <Table size="small" stickyHeader>
                          <TableHead className="table-header-color">
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  width: "50%",
                                }}
                                align="left"
                              >
                                Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  width: "50%",
                                }}
                                align="left"
                              >
                                Parameter
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filledReportParameters?.length > 0 ? (
                              <>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      display: {
                                        sm: "table-cell",
                                      },
                                      width: "50%",
                                    }}
                                  >
                                    <Typography
                                      onClick={handleOpenReportParameterDialog}
                                      sx={{
                                        cursor: "pointer",
                                        color: "primary.main",
                                        fontWeight: 700,
                                        display: "inline-block",
                                      }}
                                    >
                                      {dayjs(patientReportParameterCollWatch[0]?.entryDateTime).format(
                                        "DD-MM-YYYY hh:mm A",
                                      )}
                                    </Typography>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      display: {
                                        sm: "table-cell",
                                      },
                                      width: "50%",
                                    }}
                                  >
                                    <Typography
                                      onClick={handleOpenReportParameterDialog}
                                      sx={{
                                        cursor: "pointer",
                                        color: "primary.main",
                                        fontWeight: 700,
                                        display: "inline-block",
                                      }}
                                    >
                                      {filledReportParameters?.length}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </>
                            ) : (
                              <TableRow>
                                <TableCell colSpan={2} align="center">
                                  No record(s) found
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </form>
          </FormProvider>
          <Dialog
            className="dialogPage"
            open={
              customPopUp.open && customPopUp.code !== "MEDICAL_CONDITION_NAME"
            }
            onClose={(e, reason) => {
              if (reason !== "backdropClick") {
                handleClose();
              }
            }}
            fullScreen
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
              style: {
                width: "1200px",
                height: "700px",
                maxWidth: "none",
              },
            }}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  style={{ fontWeight: 600, marginLeft: 0 }}
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6"
                >
                  Save Doctor Save
                </Typography>
              </Toolbar>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </AppBar>
            {/* <DialogContenSavet sx={{ padding: "12px 24px" }}>
            <DoctorSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
              code={customPopUp.code}
              departmentId={methods.getValues("departmentId")}
              specialityId={methods.getValues("specialityId")}
            />
          </DialogContenSavet> */}
            <Divider />
          </Dialog>
        </Box>
      </Box>

      <Dialog
        open={openDeleteDilog}
        onClose={() => {
          setOpenDeleteDilog(false);
          setSelectedModule(null);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete Patient vaccine module?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              confirmDeleteModule();
            }}
            className="button-delete"
            color="error"
          >
            Yes
          </Button>
          <Button
            onClick={(e) => {
              setOpenDeleteDilog(false);
              setSelectedModule(null);
            }}
            autoFocus
          >
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/* VACCINE MODULE DIALOG */}
      <Dialog
        className="dialogPage"
        open={openVaccineDialog}
        onClose={(e, reason) => {
          if (reason !== "backdropClick") {
            handleVaccineModuleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            width: "calc(100vw - 24px)",
            height: "min(760px, calc(100vh - 24px))",
            maxHeight: "calc(100vh - 24px)",
            maxWidth: "none",
          },
        }}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography
              style={{ fontWeight: 600, marginLeft: 0 }}
              sx={{ ml: 2, flex: 1 }}
              variant="h6"
            >
              {vaccineDialogMode === "add"
                ? "Add Vaccine Module"
                : "Edit Vaccine Module"}
            </Typography>
          </Toolbar>
          <Divider />
        </AppBar>
        <DialogContent sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
          {openVaccineDialog && (
            <PatientVaccineModuleSave
              handleClosePopUp={handleVaccineModuleSuccess}
              isForPopUp={true}
              name="Vaccine Module"
              opd={{
                patientId: curPatientId,
                patientBirthDate: methods.getValues("dateOfBirth"),
              }}
              moduleId={
                vaccineDialogMode === "edit"
                  ? editingVaccineModule?.memberVaccineModuleId
                  : undefined
              }
            />
          )}
        </DialogContent>
      </Dialog>

      {/* SPECIAL MEDICAL CONDITION CREATE DIALOG */}
      <Dialog
        className="dialogPage"
        open={customPopUp.open && customPopUp.code === "MEDICAL_CONDITION_NAME"}
        onClose={(e, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        fullScreen
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            width: "950px",
            height: "380px",
            maxWidth: "none",
          },
        }}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography
              style={{ fontWeight: 600, marginLeft: 0 }}
              sx={{ ml: 2, flex: 1 }}
              variant="h6"
            >
              Save Special Medical Condition
            </Typography>
          </Toolbar>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </AppBar>
        <DialogContent sx={{ padding: "12px 24px" }}>
          <SpecialMedicalConditionMasterSave
            handleClosePopUp={(code, option) => {
              handleManageMedicalConditionPopup(code, option);
            }}
            isForPopUp={customPopUp.open}
            name={customPopUp.name}
            code={customPopUp.code}
            item={{
              label: customPopUp.name,
              value: "",
            }}
          />
        </DialogContent>
        <Divider />
      </Dialog>

      {/* Report Parameter Dialog */}
      <Dialog
        className="dialogPage"
        open={openReportParameter}
        onClose={(e, reason) => {
          if (reason !== "backdropClick") {
            setOpenReportParameter(false);
          }
        }}
        fullScreen
        PaperProps={{
          style: {
            width: "1500px",
            height: "760px",
            maxWidth: "none",
          },
        }}
      >
        <AppBar
          sx={{
            position: "relative",
            backgroundColor: "#f8fafc",
          }}
        >
          <Toolbar>
            <Typography
              style={{ fontWeight: 600, marginLeft: 0 }}
              sx={{ ml: 2, flex: 1 }}
              variant="h6"
            >
              Add Report Parameter
            </Typography>
          </Toolbar>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </AppBar>
        <DialogContent sx={{ padding: "12px 24px" }}>
          <PatientReportParameter
            handleClosePopUp={(code: string, item: any) => {
              handleManagePopup(code, item);
            }}
            isForPopUp={true}
            name={customPopUp.name}
            opd={opd}
            patientId={curPatientId}
            popUpReason={"OPD_PARAMETER_NAME"}
            code="REPORT_PARAMETER"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
